/* eslint-disable no-case-declarations */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ViewportScroller, formatDate } from '@angular/common';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CookieService } from 'ngx-cookie-service';
import { JoyrideService } from 'ngx-joyride';
import { NgxSpinnerService } from 'ngx-spinner';
import { saveAs } from 'file-saver';

import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import {
  ExportProjectSummaryPayload,
  ReportsService,
} from 'libs/data/src/lib/services/report/reports.service';
import {
  ExportConnectionFailedError,
  ExportResult,
  ReportExportSignalrService,
  ReportHubMethod,
} from 'libs/data/src/lib/services/report/report-export-signalr.service';
import {
  NewReportFormatEnum,
  loaderMessage,
  noReportConfigTxt,
  sensitivityLoaderMessage,
} from 'libs/shared/src/lib/constants';
import {
  addFullStop,
  customDecode,
  customEncode,
  encrypt,
  getReportGenerationBugId,
} from '@piqet-angular/data';
import { ConfirmPayload, SwalService } from 'libs/shared/src/lib/service/swal.service';
import {
  AccessPermissionService,
  CustomModalService,
  superAdminRoleStr,
} from '@piqet-angular/shared';
import { EolNavigationService } from '../../../../../../../data/src/lib/services/shared/eol-navigation.service';
import { FeatureCodes } from '../../../../../../../shared/src/lib/constants';

import {
  ImpactCategoryOption,
  IndicatorOption,
  ReportFormatOption,
  ReportFormatUiRules,
  ReportGenerationFormData,
  ReportGenerationScenario,
  ReportTypeOption,
  VersionOption,
} from './report-generation-form.model';
import {
  EMPTY_UI_RULES,
  defaultVersionId,
  findFormat,
  findImpactCategory,
  findType,
  resolveIndicators,
  resolveLegacyReportFormatId,
  showVersionFor,
  toReportGenerationFormData,
  visibleCategories,
} from './report-generation-form.adapter';

@Component({
  selector: 'piqet-angular-project-report-generation-v2',
  templateUrl: './project-report-generation-v2.component.html',
  styleUrls: ['./project-report-generation-v2.component.scss'],
})
export class ProjectReportGenerationV2Component implements OnInit, OnDestroy {
  FeatureCodes = FeatureCodes;
  readonly NewReportFormatEnum = NewReportFormatEnum;
  noConfigTxt = noReportConfigTxt;

  formData: ReportGenerationFormData | null = null;
  reportChartForm!: FormGroup;
  sensitivityReportForm!: FormGroup;

  currentProjectId: number;
  decodeUserRole: any;
  showEdit = false;

  selectedFormat: ReportFormatOption | null = null;
  selectedType: ReportTypeOption | null = null;
  selectedImpactCategory: ImpactCategoryOption | null = null;

  reportFormats: ReportFormatOption[] = [];
  reportTypeCategories: any[] = [];
  scenarios: ReportGenerationScenario[] = [];
  functionalUnits: any[] = [];
  impactCategories: ImpactCategoryOption[] = [];
  indicators: IndicatorOption[] = [];
  versions: VersionOption[] = [];
  variationLists: ReportGenerationScenario[] = [];

  ui: ReportFormatUiRules = EMPTY_UI_RULES;
  buttonName = 'Generate';
  isShowVersion = false;
  isReportGeneratable = true;
  isEnableAllScenarioDrp = true;
  isShowScenarioNotification = false;
  selectedBaseScenario: any;
  functionalUnitText = '';
  finalSubmit = false;
  isCheckVariation = false;
  errorIndex!: number;

  incorrectValueMessage =
    'Report results may be inaccurate due to outdated EOL rates in the project. Please update them before generating results.';
  incorrectBtnName = 'Show scenarios';
  incorrectFillingValueMessage = 'Action needed: Please update the filling country.';
  incorrectFillingBtnName = 'Go to filling';
  showIncorrectEOLNotification = false;
  showIncorrectFillingNotification = false;
  inValidScenarios: any[] = [];
  incorrectFilling: any[] = [];

  isExportProjectSummary = false;
  isShowReportDetailsForExportSummary = false;
  isExporting = false;
  private exportSub?: Subscription;

  private statusTimer: ReturnType<typeof setInterval> | null = null;
  private currentStatusBase = '';
  private pendingStatusBase: string | null = null;
  private currentStatusShownAt = 0;
  private statusDotPhase = 0;
  private readonly MIN_STEP_MS = 2800;
  private readonly STATUS_TICK_MS = 600;
  private readonly exportLoaderMessage = 'Preparing your export';

  disabledButton!: boolean;
  headerBtn!: string;
  tooltipMessage = '';
  hoveredIndex: number | null = null;
  isHovered = false;
  originalImageSrc = './assets/icons/add-record.svg';
  hoverImageSrc = './assets/icons/add-record-hover.svg';
  disableImageSrc = './assets/icons/add-record-disable.svg';

  userList: any;
  private readonly LCA_REPORT_LABEL = 'LCA Report';
  private readonly REPORT_TEMPLATE_FORMAT_ID = 13;
  private readonly CUSTOMIZE_CATEGORY_ID = 7;
  private readonly SCENARIO_POPUP_THRESHOLD = 6;

  constructor(
    private formBuilder: FormBuilder,
    private sharedService: SharedService,
    private route: Router,
    private reportService: ReportsService,
    private reportExportSignalr: ReportExportSignalrService,
    private toaster: ToastrService,
    private swalService: SwalService,
    private cookieService: CookieService,
    private modal: CustomModalService,
    private accessService: AccessPermissionService,
    private eolNavService: EolNavigationService,
    public joyrideService: JoyrideService,
    private viewportScroller: ViewportScroller,
    private spinner: NgxSpinnerService
  ) {
    this.currentProjectId = Number(
      atob(decodeURIComponent(customDecode(location.href.split('/')[6])))
    );
    const userRole = this.cookieService.get('u_type');
    this.decodeUserRole = this.sharedService.decodeURIComponentParam(userRole)?.toLowerCase();
  }

  ngOnInit(): void {
    this.showEdit = this.accessService.hasPermission('edit', '', 'packaging');
    !this.joyrideService.isTourInProgress() && this.viewportScroller.scrollToPosition([0, 0]);
    this.buildForms();
    this.loadFormData();
  }

  ngOnDestroy(): void {
    this.exportSub?.unsubscribe();
    this.stopStatusTicker();
    if (this.isExporting) {
      this.spinner.hide();
    }
    this.isExporting = false;
    this.sharedService.show('');
  }

  get formArray() {
    return (this.sensitivityReportForm.get('items') as FormArray).controls;
  }

  sensitivityValue(index: number, controlName: string): any {
    const item = (this.sensitivityReportForm.get('items') as FormArray).at(index) as FormGroup;
    return item?.get(controlName)?.value;
  }

  private buildForms() {
    this.reportChartForm = this.formBuilder.group({
      reportTitle: [null],
      includeInputInformation: [false],
      newFormateList: [null, Validators.required],
      reportFormatByList: [null],
      includeScenario: [null, Validators.required],
      functionalUnit: [null, Validators.required],
      baselineScenario: [null, Validators.required],
      impactCategories: [null],
      includeCategories: [null],
      version: [null],
      additionalInformation: [null],
      functionalUnitText: [''],
    });
    this.sensitivityReportForm = this.formBuilder.group({
      items: this.formBuilder.array([]),
    });
    this.addItem();
  }

  loadFormData() {
    this.reportService.getReportGenerationFormData(this.currentProjectId).subscribe({
      next: response => this.applyFormData(toReportGenerationFormData(response)),
      error: () => this.toaster.error('Failed to load report options'),
    });
  }

  private applyFormData(data: ReportGenerationFormData) {
    this.formData = data;
    this.reportFormats = data.reportFormats;
    this.scenarios = data.scenarios;
    this.functionalUnits = data.functionalUnits;
    this.impactCategories = data.impactCategories;
    this.indicators = data.indicators;
    this.versions = data.versions;
    this.variationLists = data.scenarios;

    this.inValidScenarios = data.invalidEolScenarios;
    this.showIncorrectEOLNotification = this.inValidScenarios.length > 0;
    this.incorrectBtnName = this.inValidScenarios.length > 1 ? 'Show scenarios' : 'Go to scenario';

    this.incorrectFilling = data.incorrectFillingScenarios;
    this.showIncorrectFillingNotification = this.incorrectFilling.length > 0;
    this.incorrectFillingBtnName =
      this.incorrectFilling.length > 1 ? 'Show scenarios' : 'Go to filling';

    this.functionalUnitText = data.project.functionalUnitText ?? '';
    this.reportChartForm.patchValue({
      reportTitle: data.defaults.reportTitle || data.project.name,
      functionalUnitText: this.functionalUnitText,
      includeInputInformation: data.defaults.isIncludeInputInformation,
    });

    const format =
      findFormat(data, data.defaults.reportFormatId) ??
      this.reportFormats.find(x => !x.disabled) ??
      null;
    this.applyFormatSelection(format, false);

    const type = findType(
      format,
      data.defaults.reportTypeId,
      data.defaults.isCustomType,
      data.defaults.legacyReportFormatId
    );
    if (type) {
      this.applyTypeSelection(type, false);
    }

    this.patchSavedSelection();
  }

  private patchSavedSelection() {
    if (!this.formData) return;
    const defaults = this.formData.defaults;

    const scenarioIds = defaults.scenarioIds.filter(id =>
      this.scenarios.some(x => x.idStr === id)
    );
    const baseScenario = this.scenarios.find(
      x => Number(x.idStr) === Number(defaults.baseScenarioId)
    );
    const functionalUnit = this.functionalUnits.find(
      x => Number(x.id) === Number(defaults.functionalUnitId)
    );
    const impactCategory = findImpactCategory(this.formData, defaults.impactCategoryId);

    this.reportChartForm.patchValue({
      includeScenario: scenarioIds.length ? scenarioIds : null,
      baselineScenario: baseScenario ?? null,
      functionalUnit: functionalUnit ?? null,
      impactCategories: impactCategory ?? null,
      includeCategories: defaults.indicatorIds.length ? defaults.indicatorIds : null,
    });

    this.selectedImpactCategory = impactCategory;
    this.applyCategorySelectionValidators();

    this.selectedBaseScenario = baseScenario?.idStr;
    this.isEnableAllScenarioDrp = !baseScenario;

    if (defaults.sensitivityAnalyses.length) {
      const itemsArray = this.sensitivityReportForm.get('items') as FormArray;
      itemsArray.clear();
      defaults.sensitivityAnalyses.forEach(x => this.addItem(x));
      if (!itemsArray.length) this.addItem();
    }
  }

  onChangeReportFormat(event: ReportFormatOption) {
    if (!event) return;
    this.applyFormatSelection(event, true);
  }

  private applyFormatSelection(format: ReportFormatOption | null, resetType: boolean) {
    this.selectedFormat = format;
    this.ui = format?.ui ?? EMPTY_UI_RULES;
    this.reportTypeCategories = visibleCategories(format);
    this.buttonName = format?.generateButtonLabel ?? 'Generate';

    this.isExportProjectSummary =
      format?.id === NewReportFormatEnum.ExportProjectSummaryQuadpack ||
      format?.id === NewReportFormatEnum.ExportProjectSummaryAllOrganization;
    this.isShowReportDetailsForExportSummary =
      format?.id === NewReportFormatEnum.ExportProjectSummaryAllOrganization;

    this.reportChartForm.patchValue({ newFormateList: format }, { emitEvent: false });

    if (resetType) {
      this.selectedType = null;
      this.reportChartForm.controls['reportFormatByList'].reset();
      this.indicators = this.formData?.indicators ?? [];
      this.reportChartForm.controls['includeCategories'].setValue(null);
      this.reportChartForm.patchValue({
        reportTitle: this.formData?.project.name,
      });
    }

    this.isReportGeneratable = true;
    this.applyVersionVisibility();
    this.applyValidators();
  }

  onChangeReportType(event: ReportTypeOption) {
    if (!event) return;
    this.applyTypeSelection(event, true);
  }

  private applyTypeSelection(type: ReportTypeOption, resetCategories: boolean) {
    this.selectedType = type;
    this.isReportGeneratable = type.isReportGeneratable;
    if (!this.isReportGeneratable) this.toaster.warning(this.noConfigTxt);

    this.reportChartForm.patchValue({ reportFormatByList: type }, { emitEvent: false });

    this.indicators = resolveIndicators(
      this.formData ?? ({ indicators: [] } as any),
      type
    );
    if (resetCategories) {
      this.reportChartForm.controls['includeCategories'].setValue(null);
    }

    this.applyVersionVisibility();
    this.applyValidators();
  }

  get selectedIndicatorIds(): number[] {
    const value = this.reportChartForm.controls['includeCategories'].value;
    return Array.isArray(value) ? value.map(Number) : [];
  }

  private isPickerChrome(event: Event): boolean {
    const target = event.target as HTMLElement | null;
    return Boolean(target?.closest('.ng-value-icon') || target?.closest('.ng-clear-wrapper'));
  }

  onCategoryPickerClick(event: Event) {
    if (this.isPickerChrome(event)) return;
    this.openIndicatorSelectModal();
  }

  openIndicatorSelectModal() {
    this.modal.invoke('indicatorSelect', {
      class: 'modal-lg',
      initialState: {
        items: this.indicators,
        selectedIds: this.selectedIndicatorIds,
        maxSelection: this.ui.maxCategories,
        title: 'Categories to include',
        itemNoun: 'categories',
      },
    });
    this.modal.ref?.content.selectionConfirmed.subscribe((ids: number[]) => {
      const control = this.reportChartForm.controls['includeCategories'];
      control.setValue(ids?.length ? ids : null);
      control.markAsTouched();
      control.updateValueAndValidity();
    });
  }

  get selectedScenarioIds(): string[] {
    const value = this.reportChartForm.controls['includeScenario'].value;
    if (Array.isArray(value)) return value.map(String);
    return value ? String(value).split(',').filter(Boolean) : [];
  }

  get useScenarioPopup(): boolean {
    return this.scenarios.length > this.SCENARIO_POPUP_THRESHOLD;
  }

  get scenarioPickerIsOpen(): boolean | undefined {
    return this.useScenarioPopup ? false : undefined;
  }

  onScenarioPickerClick(event: Event) {
    this.enableAllScenarioDrp();
    if (!this.useScenarioPopup) return;
    if (this.ui.showBaseScenario && this.isEnableAllScenarioDrp) return;
    if (this.isPickerChrome(event)) return;
    this.openScenarioSelectModal();
  }

  openScenarioSelectModal() {
    this.modal.invoke('indicatorSelect', {
      class: 'modal-lg',
      initialState: {
        items: this.scenarios.map(x => ({ id: x.idStr, name: x.description })),
        selectedIds: this.selectedScenarioIds,
        maxSelection: this.ui.maxScenarios,
        title: 'Scenarios to include',
        itemNoun: 'scenarios',
        highlightId: this.selectedBaseScenario ?? null,
        highlightLabel: 'Base scenario',
      },
    });
    this.modal.ref?.content.selectionConfirmed.subscribe((ids: string[]) => {
      const control = this.reportChartForm.controls['includeScenario'];
      control.setValue(ids?.length ? ids : null);
      control.markAsTouched();
      control.updateValueAndValidity();
    });
  }

  onChangeImpactCategory(event: ImpactCategoryOption) {
    if (event?.disabled) {
      setTimeout(() => this.reportChartForm.controls['impactCategories'].setValue(null), 0);
      return;
    }
    this.selectedImpactCategory = event ?? null;
    this.reportChartForm.controls['includeCategories'].setValue(null);
    this.applyCategorySelectionValidators();
  }

  private applyCategorySelectionValidators() {
    const control = this.reportChartForm.get('includeCategories');
    if (this.showCategoriesToInclude) {
      control?.setValidators([Validators.required]);
    } else {
      control?.clearValidators();
      control?.setValue(null);
    }
    control?.updateValueAndValidity();
  }

  private applyVersionVisibility() {
    this.isShowVersion = showVersionFor(this.selectedFormat, this.selectedType, this.versions);
    const control = this.reportChartForm.controls['version'];
    if (this.isShowVersion) {
      control.setValidators([Validators.required]);
      if (control.value == null) {
        const preferred = this.formData?.defaults.versionId ?? defaultVersionId(this.versions);
        preferred != null && control.setValue(preferred);
      }
    } else {
      control.clearValidators();
      control.setValue(null);
    }
    control.updateValueAndValidity();
  }

  private applyValidators() {
    const setRequired = (name: string, required: boolean) => {
      const control = this.reportChartForm.controls[name];
      if (!control) return;
      required ? control.setValidators([Validators.required]) : control.clearValidators();
      control.updateValueAndValidity();
    };

    if (this.isExportProjectSummary) {
      for (const name of [
        'reportFormatByList',
        'impactCategories',
        'includeCategories',
        'functionalUnit',
        'baselineScenario',
        'functionalUnitText',
        'reportTitle',
      ]) {
        const control = this.reportChartForm.controls[name];
        if (!control) continue;
        control.reset();
        control.clearValidators();
        control.updateValueAndValidity();
      }
      this.reportChartForm.controls['includeScenario']?.enable();
      this.isEnableAllScenarioDrp = false;
      this.isShowScenarioNotification = false;
      return;
    }

    setRequired('reportFormatByList', this.ui.showReportType);
    setRequired('reportTitle', this.ui.showReportTitle);
    setRequired('functionalUnit', this.ui.showFunctionalUnit);
    setRequired('baselineScenario', this.ui.showBaseScenario);
    setRequired('impactCategories', this.showImpactCategory);
    setRequired('functionalUnitText', Boolean(this.selectedType?.requiresFunctionalUnitText));
    this.applyCategorySelectionValidators();
  }

  get showImpactCategory(): boolean {
    return this.ui.showImpactCategory && !this.selectedType?.isCustom;
  }

  get showCategoriesToInclude(): boolean {
    return (
      this.ui.showCategoriesToInclude &&
      !this.selectedType?.isCustom &&
      Boolean(this.selectedImpactCategory?.requiresCategorySelection)
    );
  }

  get maxScenarios(): number {
    return this.ui.maxScenarios ?? this.scenarios.length;
  }

  get maxCategories(): number {
    return this.ui.maxCategories ?? this.indicators.length;
  }

  get isCustomType(): boolean {
    return Boolean(this.selectedType?.isCustom);
  }

  get legacyReportFormatId(): number {
    return resolveLegacyReportFormatId(this.selectedFormat, this.selectedType);
  }

  get isCheckISOReport(): boolean {
    return Boolean(this.selectedType?.requiresFunctionalUnitText);
  }

  get isShowSensitivityAnalysis(): boolean {
    return this.ui.showSensitivityAnalysis;
  }

  get isShareVisible(): boolean {
    return (
      this.selectedFormat?.name === this.LCA_REPORT_LABEL &&
      !this.isExportProjectSummary &&
      this.legacyReportFormatId !== this.REPORT_TEMPLATE_FORMAT_ID
    );
  }

  commonInputSearchName(term: string, item: any) {
    return item.name.toLowerCase().includes(term.toLowerCase());
  }

  commonInputSearchDes(term: string, item: any) {
    return item.description.toLowerCase().includes(term.toLowerCase());
  }

  triggerScroll(): void {
    this.sharedService.triggerScroll();
  }

  enableAllScenarioDrp() {
    if (this.reportChartForm.controls['baselineScenario'].invalid) {
      this.isShowScenarioNotification = true;
      this.isEnableAllScenarioDrp = true;
    }
  }

  changeBaseScenario(event: { idStr?: string }): void {
    if (!event?.idStr) return;
    this.isShowScenarioNotification = false;
    this.isEnableAllScenarioDrp = false;
    this.selectedBaseScenario = event.idStr;
  }

  onFunctionalUnitChange(): void {
    this.functionalUnitText = this.reportChartForm.get('functionalUnitText')?.value || '';
  }

  onSaveFunctionalUnitText() {
    if (this.isCheckISOReport && this.reportChartForm.controls['functionalUnitText'].invalid) {
      return;
    }
    const trimmedValue = this.reportChartForm.get('functionalUnitText')?.value?.trim() || null;
    const payload = {
      projectId: Number(this.currentProjectId),
      functionalUnitText: trimmedValue ? addFullStop(trimmedValue) : null,
    };
    this.reportService.saveFunctionalUnitText(payload).subscribe((res: any) => {
      const msgFn = res.messageType ? this.toaster.error : this.toaster.success;
      msgFn.call(this.toaster, res.message);
    });
  }

  eolAction(event: any) {
    this.eolNavService.eolAction(
      event,
      event == 'fill-data-list' ? this.incorrectFilling : this.inValidScenarios,
      this.currentProjectId,
      scenario => this.eolNavService.navigateToScenario(scenario, this.currentProjectId, event)
    );
  }

  onPrev(step: string) {
    if (step == 'adv-14') {
      const projectId = this.currentProjectId;
      this.route.navigate(
        [
          `projects/projects-setting/${customEncode(
            btoa(encodeURIComponent(projectId))
          )}/interpretation`,
        ],
        { queryParams: { qPrm: 'UsawqPnfsdQnxc' } }
      );
      localStorage.setItem('scenarioName', 'Interpretation');
    }
  }

  onNext(step: string) {}

  selectAddRefrences(event: any, index: number) {
    if (event?.idStr) {
      this.variationLists = this.scenarios.filter(x => x.idStr != event.idStr);
      const item = (this.sensitivityReportForm.get('items') as FormArray).at(index) as FormGroup;
      if (item) {
        item.controls['variationOne'].setValue(null);
        item.controls['variationTwo'].setValue(null);
        item.controls['variationThree'].setValue(null);
        item.controls['variationFour'].setValue(null);
      }
    }
  }

  resetCalculations(index: number) {
    const item = (this.sensitivityReportForm.get('items') as FormArray).at(index) as FormGroup;
    if (item) {
      item.controls['variationOne'].setValue(null);
      item.controls['variationTwo'].setValue(null);
      item.controls['variationThree'].setValue(null);
      item.controls['variationFour'].setValue(null);
    }
  }

  checkVariationValue(event: any, i: number, seq: number) {
    if (event?.idStr) {
      this.isCheckVariation = false;
    }
  }

  changeVariation(event: any, i: number, seq?: number) {
    const item: any = (this.sensitivityReportForm.get('items') as FormArray).at(i) as FormGroup;
    if (!item.get('addRefrences').value) {
      this.variationLists = [];
      return;
    }
    const variationKeys = ['variationOne', 'variationTwo', 'variationThree', 'variationFour'];
    const valueToRemove =
      seq && variationKeys[seq - 1] ? item.get(variationKeys[seq - 1]).value : null;
    const idList = Object.values(item.value);
    const indexToRemove = idList.indexOf(valueToRemove);
    if (indexToRemove !== -1) {
      idList.splice(indexToRemove, 1);
    }
    const filteredIdList = idList.filter(x => x !== null);
    this.variationLists = this.scenarios.filter(x => !filteredIdList.includes(x.idStr));
  }

  addItem(itemData?: any) {
    const itemsArrayCheckValue = this.sensitivityReportForm.get('items') as FormArray;
    for (let i = 0; i < itemsArrayCheckValue.length; i++) {
      const item: any = itemsArrayCheckValue.at(i) as FormGroup;
      if (
        !item?.get('addRefrences').value ||
        (!item.get('variationOne').value &&
          !item.get('variationTwo').value &&
          !item.get('variationThree').value &&
          !item.get('variationFour').value)
      ) {
        return;
      }
    }

    const reference = itemData?.referenceScenarioId;
    this.variationLists = this.scenarios.filter(x => x.idStr != String(reference));

    const isKnownScenario = (id: any) =>
      id != null && this.scenarios.some(x => x.idStr === String(id));
    const variations: any[] = itemData?.variationIds ?? [];
    const variationAt = (index: number) => {
      const value = variations[index];
      return isKnownScenario(reference) && isKnownScenario(value) ? String(value) : null;
    };

    const itemFormGroup = this.formBuilder.group({
      addRefrences: [isKnownScenario(reference) ? String(reference) : null],
      variationOne: [variationAt(0)],
      variationTwo: [variationAt(1)],
      variationThree: [variationAt(2)],
      variationFour: [variationAt(3)],
    });

    (this.sensitivityReportForm.get('items') as FormArray).push(itemFormGroup);
  }

  removeSensitivityItem(index: number) {
    (this.sensitivityReportForm.get('items') as FormArray).removeAt(index);
    this.setHoveredIndex(null);
  }

  setHoveredIndex(index: number | null): void {
    this.hoveredIndex = index;
  }

  changeImageOnHover(isHovered: boolean): void {
    this.isHovered = isHovered;
    const src = this.disabledButton
      ? this.disableImageSrc
      : isHovered
      ? this.hoverImageSrc
      : this.originalImageSrc;
    this.headerBtn = `<img class="fa fa-plus" width="45" height="45" src="${src}">`;
  }

  checkDisabledRef() {
    this.disabledButton = false;
    this.tooltipMessage = '';

    const itemsArray = this.sensitivityReportForm.get('items') as FormArray;
    const isSuperAdmin = this.decodeUserRole === superAdminRoleStr;

    const isInvalidItem = (item: FormGroup): boolean =>
      !item?.get('addRefrences')?.value ||
      (!item.get('variationOne')?.value &&
        !item.get('variationTwo')?.value &&
        !item.get('variationThree')?.value &&
        !item.get('variationFour')?.value);

    if (!isSuperAdmin && itemsArray.length >= 2) {
      this.disabledButton = true;
      this.tooltipMessage = 'You cannot add more than two sensitivity analyses.';
    } else {
      for (let i = 0; i < itemsArray.length; i++) {
        if (isInvalidItem(itemsArray.at(i) as FormGroup)) {
          this.disabledButton = true;
          break;
        }
      }
    }

    const imageSrc = this.disabledButton
      ? this.disableImageSrc
      : this.isHovered
      ? this.hoverImageSrc
      : this.originalImageSrc;
    this.headerBtn = `<img class="fa fa-plus" width="45" height="45" src="${imageSrc}">`;

    return this.disabledButton;
  }

  private buildSensitivityPayload(): any[] | null {
    const itemsArray = this.sensitivityReportForm.get('items') as FormArray;
    const result: any[] = [];
    if (!itemsArray) return result;

    for (let i = 0; i < itemsArray.length; i++) {
      const item = itemsArray.at(i) as FormGroup;
      const hasAny =
        item?.get('addRefrences')?.value ||
        item?.get('variationOne')?.value ||
        item?.get('variationTwo')?.value ||
        item?.get('variationThree')?.value ||
        item?.get('variationFour')?.value;
      if (!hasAny) continue;

      const sensitivityAnalysis: any = {
        referenceSensitivityAnalysis: item.get('addRefrences')?.value,
        variationOne: item.get('variationOne')?.value,
        variationTwo: item.get('variationTwo')?.value,
        variationThree: item.get('variationThree')?.value,
        variationFour: item.get('variationFour')?.value,
      };

      const variations = ['variationOne', 'variationTwo', 'variationThree', 'variationFour'];
      for (let v = 0; v < variations.length - 1; v++) {
        if (
          sensitivityAnalysis[variations[v]] === null &&
          sensitivityAnalysis[variations[v + 1]] !== null
        ) {
          sensitivityAnalysis[variations[v]] = sensitivityAnalysis[variations[v + 1]];
          sensitivityAnalysis[variations[v + 1]] = null;
        }
      }

      if (
        !!sensitivityAnalysis.referenceSensitivityAnalysis &&
        !item?.get('variationOne')?.value &&
        !item?.get('variationTwo')?.value &&
        !item?.get('variationThree')?.value &&
        !item?.get('variationFour')?.value
      ) {
        this.isCheckVariation = true;
        this.errorIndex = i;
        return null;
      }
      result.push(sensitivityAnalysis);
    }
    return result;
  }

  private buildPayload(): any | null {
    const baseScenarioId = Number(this.reportChartForm.controls['baselineScenario'].value.idStr);
    let scenariosList = this.reportChartForm.controls['includeScenario'].value || [];
    if (!Array.isArray(scenariosList)) {
      scenariosList = scenariosList.toString().split(',');
    }
    scenariosList = scenariosList.map((id: any) => id.toString());

    const formatId = this.selectedFormat?.id;
    if (formatId === NewReportFormatEnum.LCAReport) {
      const existingIndex = scenariosList.indexOf(baseScenarioId.toString());
      if (existingIndex !== -1) {
        scenariosList.splice(existingIndex, 1);
      }
      scenariosList.unshift(baseScenarioId.toString());
    } else if (formatId === NewReportFormatEnum.LCAPresentation) {
      if (scenariosList.length > 1 && scenariosList.includes(baseScenarioId.toString())) {
        scenariosList = scenariosList.filter((id: any) => id !== baseScenarioId.toString());
      }
    }

    const allAdditionalIds = (this.formData?.additionalCategories ?? [])
      .map(x => x.id)
      .join(',');
    const selectedCategories = this.reportChartForm.controls['includeCategories'].value;
    const functionalUnitId = this.reportChartForm.controls['functionalUnit'].value.id;

    const impactCategoryId = this.isCustomType
      ? this.CUSTOMIZE_CATEGORY_ID
      : this.reportChartForm.controls['impactCategories'].value.id;

    const usesSelectedIndicators = Number(impactCategoryId) === this.CUSTOMIZE_CATEGORY_ID;
    const selectedIndicatorIds =
      !!selectedCategories && selectedCategories.length !== 0
        ? selectedCategories.toString()
        : this.indicators.map(x => x.id).join(',');

    const payload: any = {
      projectId: Number(this.currentProjectId),
      reportFormatId: this.legacyReportFormatId,
      newReportFormatId: this.selectedFormat?.id,
      newReportTypeId: this.selectedType?.id ?? 0,
      version: this.isShowVersion ? this.reportChartForm.controls['version'].value : null,
      scenarioIds: scenariosList.toString(),
      baseScenarioId: baseScenarioId,
      impactCategoryId: impactCategoryId,
      indicatorsId: usesSelectedIndicators ? selectedIndicatorIds : '',
      functionUnitId: functionalUnitId,
      additionalElements: this.ui.showIncludeInputInformation ? '0' : allAdditionalIds,
      isExcludeProductImpacts: false,
      isIncludeInputInformation: this.reportChartForm.controls['includeInputInformation'].value,
      reportName: this.reportChartForm.controls['reportTitle']?.value,
      indicatorId: '',
      scenarioId: baseScenarioId,
      isExport: this.ui.showIncludeInputInformation ? true : false,
      isNavigate: false,
      isCustomType: this.isCustomType,
      reportTypeId: functionalUnitId,
      equivalentPackageScenarioId: 0,
      reportGeneratedDate: formatDate(Date.now(), 'yyyy-MM-dd HH:mm', 'en-US'),
    };

    const sensitivityList = this.buildSensitivityPayload();
    if (sensitivityList === null) return null;
    payload['sensitivityAnalysisList'] = sensitivityList;

    if (this.ui.showIncludeInputInformation) {
      payload['userId'] = this.cookieService.get('userId');
    }
    payload.reportType = this.legacyReportFormatId;
    payload.loaderMessage =
      sensitivityList.length > 0
        ? `Adding a sensitivity analysis requires more load time... <br> ${sensitivityLoaderMessage}`
        : loaderMessage;

    return payload;
  }

  async onGenerateReport(type?: string) {
    this.finalSubmit = true;
    if (this.reportChartForm.invalid) return;

    const payload = this.buildPayload();
    if (!payload) return;

    const scenarioCount = payload.scenarioIds ? payload.scenarioIds.split(',').length : 0;
    const hasSensitivityAnalysis = payload.sensitivityAnalysisList.length > 0;
    const isShareable =
      this.selectedFormat?.id !== NewReportFormatEnum.LCAPresentation &&
      this.selectedFormat?.id !== NewReportFormatEnum.ExportRawData;

    if (
      (scenarioCount >= 4 || hasSensitivityAnalysis) &&
      type !== 'save' &&
      type !== 'shareReport' &&
      isShareable &&
      this.legacyReportFormatId != this.REPORT_TEMPLATE_FORMAT_ID
    ) {
      const swalPayload: ConfirmPayload = {
        title: 'Report generation',
        text: `Your report may take some time to generate. Would you like us to send it directly to your email instead of waiting here?`,
        icon: 'warning',
        iconHtml: '',
        showCancelButton: true,
        confirmButtonText: 'Email me',
        cancelButtonText: 'Wait here',
        reverseButtons: true,
        allowOutsideClick: false,
        customClass: 'Popupbackground email-report-confirmation',
        iconColor: '#FFFFFF',
        enableAnimation: false,
      };
      const confirmed = await this.swalService.confirmLegacy(swalPayload);
      if (confirmed) {
        return this.openShareModal();
      }
    }

    switch (this.legacyReportFormatId) {
      case 3:
      case 4:
      case 11:
      case 12:
      case 0:
        if (type == 'save') {
          await this.saveReportDetails(payload);
        } else {
          this.attachDispatchFlags(payload);
          if (this.legacyReportFormatId === 0) {
            payload.reportTypeIdByList = 0;
            payload.reportFormatId = 0;
          }
          if (type != 'shareReport') {
            if (this.legacyReportFormatId === 12) {
              payload['APIToken'] = this.cookieService.get('token');
              payload.indicatorId = payload.indicatorId == '' ? 0 : payload.indicatorId;
              payload.isReportGenerated = false;
              payload.isExport = true;
              this.downloadPDF(
                payload,
                'PDFGenerator/ProductLCAReport?requestString',
                '_SummaryReport'
              );
            } else {
              this.openReportTab(payload);
            }
          } else {
            this.pdfExport(payload);
          }
        }
        break;
      case 5:
        this.sharedService.show(loaderMessage);
        this.spinner.show();
        const responseCsv = await this.runReportJob('StartExportCsv', payload, () =>
          this.reportService.getExcelSummaryLink(payload)
        );
        this.spinner.hide();
        this.sharedService.show('');
        this.handleDownloadResult(payload, responseCsv);
        break;
      case 6:
        if (type == 'save') {
          const responseProductWaste = await this.reportService.generateProductWasteReport(payload);
          responseProductWaste.messageType == 0
            ? this.toaster.success(responseProductWaste.message)
            : this.toaster.error(responseProductWaste.message);
        } else {
          this.attachDispatchFlags(payload);
          this.route.navigate(['projects/project-detail-report'], {
            queryParams: { payload: encrypt(JSON.stringify(payload)) },
          });
        }
        break;
      case 7:
        payload['APIToken'] = this.cookieService.get('token');
        payload.indicatorId = payload.indicatorId == '' ? 0 : payload.indicatorId;
        this.downloadPDF(payload, 'PDFGenerator/SlideDeckReports?reuqestString', '_LCA Presentation');
        break;
      case 8:
        if (type == 'save') {
          const responseCarbon = await this.reportService.generateCarbonFootprintReport(payload);
          responseCarbon.messageType == 0
            ? this.toaster.success(responseCarbon.message)
            : this.toaster.error(responseCarbon.message);
        } else {
          this.attachDispatchFlags(payload);
          this.route.navigate(['projects/project-detail-report'], {
            queryParams: { payload: encrypt(JSON.stringify(payload)) },
          });
        }
        break;
      case 10:
        payload['APIToken'] = this.cookieService.get('token');
        payload.indicatorId = payload.indicatorId == '' ? 0 : payload.indicatorId;
        this.downloadPDF(payload, 'PDFGenerator/SlideReport?reuqestString', '_LCA Scenario Slides');
        break;
      case 13:
        if (type == 'save') {
          await this.saveReportDetails(payload);
        } else {
          this.attachDispatchFlags(payload);
          payload.templateId = this.selectedType?.id;
          if (type != 'shareReport') {
            const queryString = this.toQueryString({
              payload: encrypt(JSON.stringify(payload)),
              templateId: this.selectedType?.id,
            });
            window.open(
              `/#/report-builder/report-preview/${this.selectedType?.id}?source=project&${queryString}`,
              '_blank'
            );
          } else {
            this.pdfExport(payload);
          }
        }
        break;
      case 14:
        this.sharedService.show(loaderMessage);
        this.spinner.show();
        const responseCsvNew = await this.runReportJob('StartExportCsvNew', payload, () =>
          this.reportService.getProductExcelSummaryLinkNew(payload)
        );
        this.spinner.hide();
        this.sharedService.show('');
        this.handleDownloadResult(payload, responseCsvNew);
        break;
    }
    this.finalSubmit = false;
  }

  private async saveReportDetails(payload: any) {
    const response = await this.reportService.saveLCAReportDetails(payload);
    response.messageType == 0
      ? this.toaster.success('Report details have been successfully saved.')
      : this.toaster.error(response.message);
  }

  private attachDispatchFlags(payload: any) {
    payload.flag = this.isCustomType;
    payload.isCheckeportListByTypeById = this.selectedType?.id ?? 0;
    payload.isCheckeportListByFormateById = this.selectedFormat?.id;
  }

  private toQueryString(params: Record<string, any>): string {
    return Object.keys(params)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&');
  }

  private openReportTab(payload: any) {
    const queryString = this.toQueryString({ payload: encrypt(JSON.stringify(payload)) });
    window.open(`/#/projects/project-detail-report?${queryString}`, '_blank');
  }

  private handleDownloadResult(payload: any, response: any) {
    if (response.messageType === 0) {
      const downloadLink = document.createElement('a');
      downloadLink.href = response.data;
      downloadLink.download = 'report.pdf';
      downloadLink.click();
    } else {
      this.errorGenerateReport(payload, response);
    }
  }

  onExportProjectSummary() {
    const rawSelection = this.reportChartForm.controls['includeScenario']?.value;
    let rawScenarioIds: any[] = [];
    if (Array.isArray(rawSelection)) {
      rawScenarioIds = rawSelection;
    } else if (rawSelection !== null && rawSelection !== undefined && rawSelection !== '') {
      rawScenarioIds = String(rawSelection)
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);
    }

    const scenarioIds: number[] = rawScenarioIds
      .map((id: any) => Number(id))
      .filter((id: number) => Number.isFinite(id));

    const payload: ExportProjectSummaryPayload = {
      projectId: Number(this.currentProjectId),
      scenarioIds: scenarioIds,
      isShowReportDetails: this.isShowReportDetailsForExportSummary,
    };

    if (this.isExporting) return;
    this.isExporting = true;
    this.spinner.show();
    this.startStatusTicker(this.exportLoaderMessage);

    this.exportSub = this.reportExportSignalr.exportProject(payload).subscribe({
      next: ev => {
        if (ev.kind === 'progress') {
          this.enqueueStatus(ev.message || this.exportLoaderMessage);
        } else {
          this.finishExport();
          this.downloadExportResult(ev.result);
        }
      },
      error: err => {
        if (err instanceof ExportConnectionFailedError) {
          this.exportProjectSummaryHttpFallback(payload);
        } else {
          this.finishExport();
          this.toaster.error('Failed to export project summary');
        }
      },
    });
  }

  private finishExport() {
    this.isExporting = false;
    this.stopStatusTicker();
    this.spinner.hide();
    this.sharedService.show('');
  }

  private startStatusTicker(initialMessage: string) {
    this.stopStatusTicker();
    this.currentStatusBase = this.stripTrailingDots(initialMessage);
    this.pendingStatusBase = null;
    this.currentStatusShownAt = Date.now();
    this.statusDotPhase = 0;
    this.renderStatus();
    this.statusTimer = setInterval(() => this.tickStatus(), this.STATUS_TICK_MS);
  }

  private enqueueStatus(message: string) {
    const base = this.stripTrailingDots(message);
    if (!base || base === this.currentStatusBase) return;
    this.pendingStatusBase = base;
  }

  private tickStatus() {
    if (!this.isExporting) return;
    if (
      this.pendingStatusBase !== null &&
      Date.now() - this.currentStatusShownAt >= this.MIN_STEP_MS
    ) {
      this.currentStatusBase = this.pendingStatusBase;
      this.pendingStatusBase = null;
      this.currentStatusShownAt = Date.now();
      this.statusDotPhase = 0;
    } else {
      this.statusDotPhase = (this.statusDotPhase + 1) % 4;
    }
    this.renderStatus();
  }

  private renderStatus() {
    this.sharedService.show(this.currentStatusBase + '.'.repeat(this.statusDotPhase));
  }

  private stopStatusTicker() {
    if (this.statusTimer) {
      clearInterval(this.statusTimer);
      this.statusTimer = null;
    }
  }

  private stripTrailingDots(message: string): string {
    return (message || '').replace(/[.…\s]+$/, '');
  }

  private runReportJob(
    hubMethod: ReportHubMethod,
    payload: any,
    httpFallback: () => Promise<any>
  ): Promise<any> {
    return new Promise(resolve => {
      let resolved = false;
      const done = (value: any) => {
        if (resolved) return;
        resolved = true;
        resolve(value);
      };

      this.exportSub = this.reportExportSignalr.generateReport(hubMethod, payload).subscribe({
        next: ev => {
          if (ev.kind === 'progress') {
            this.sharedService.show(ev.message || payload.loaderMessage || 'Working…');
          } else {
            done({
              messageType: ev.result.messageType,
              data: ev.result.data ? JSON.parse(ev.result.data) : null,
              message: ev.result.message,
            });
          }
        },
        error: err => {
          if (err instanceof ExportConnectionFailedError) {
            httpFallback()
              .then(done)
              .catch(() => done({ messageType: 1, message: 'Failed to generate report' }));
          } else {
            done({ messageType: 1, message: (err && err.message) || 'Failed to generate report' });
          }
        },
      });
    });
  }

  private downloadExportResult(result: ExportResult) {
    if (result?.messageType === 0 && result.data) {
      this.downloadAndCleanup(result.data, result.fileName || this.fileNameFromUrl(result.data));
    } else {
      this.toaster.error('Failed to export project summary');
    }
  }

  private downloadAndCleanup(url: string, fileName: string) {
    const name = fileName || 'project_summary.xlsm';
    fetch(url)
      .then(res => {
        if (!res.ok) {
          throw new Error(`Download failed with status ${res.status}`);
        }
        return res.blob();
      })
      .then(blob => {
        saveAs(blob, name);
        this.reportService.deleteExportProjectFile(name).subscribe({
          error: () => {
            /* best-effort cleanup */
          },
        });
      })
      .catch(() => {
        const downloadLink = document.createElement('a');
        downloadLink.href = url;
        downloadLink.download = name;
        downloadLink.click();
      });
  }

  private fileNameFromUrl(url: string): string {
    const lastSegment = (url || '').split('/').pop() || '';
    return lastSegment.split('?')[0] || 'project_summary.xlsm';
  }

  private exportProjectSummaryHttpFallback(payload: ExportProjectSummaryPayload) {
    this.reportService.exportProjectSummary(payload).subscribe({
      next: (response: any) => {
        this.finishExport();
        if (response?.messageType === 0) {
          this.downloadAndCleanup(response.data, this.fileNameFromUrl(response.data));
        } else {
          this.toaster.error('Failed to export project summary');
        }
      },
      error: () => {
        this.finishExport();
        this.toaster.error('Failed to export project summary');
      },
    });
  }

  errorGenerateReport(payload: any, response: any) {
    this.swalService
      .confirm(
        'Report this issue',
        'An error occurred while generating your report. Do you want to report this issue to the Piqet team?'
      )
      .then(confirmed => {
        if (confirmed) {
          this.openReportBugModal(payload, response);
        }
      });
  }

  openReportBugModal(payload: any, response: any) {
    this.modal.invoke('reportBug', {
      initialState: {
        payload,
        response,
        categoryInfo: getReportGenerationBugId(),
      },
      class: 'modal-xl',
    });
  }

  downloadPDF(payload: any, generateURL: any, type: any) {
    this.sharedService.show(loaderMessage);
    this.reportService.directGenertePDF(payload, generateURL).subscribe(
      blob => {
        this.reportService.processPdfBlobResponse(blob, {
          payload: payload,
          fileName: payload.reportName + type,
          fileExtension: '.pdf',
          onError: (errorPayload, error) => {
            this.errorGenerateReport(errorPayload, error);
            this.sharedService.show('');
          },
          onSuccess: () => {
            this.sharedService.show('');
          },
        });
      },
      error => {
        this.errorGenerateReport(payload, error);
        this.sharedService.show('');
      }
    );
  }

  openShareModal() {
    this.finalSubmit = true;
    if (this.reportChartForm.invalid) return;
    this.modal.invoke('ReportShare', { class: 'modal-lg' });
    this.modal.ref?.content.exportOrShareReport.subscribe((exportOrShareReport: any) => {
      this.userList = exportOrShareReport.payload;
      this.onGenerateReport('shareReport');
    });
  }

  sendReportMail(payload: any, generateURL: any) {
    const newPayload = { ...payload, userList: this.userList };
    this.reportService.directGenertePDF(newPayload, generateURL).subscribe({
      next: blob => {
        this.reportService.processMailReportResponse(blob, {
          payload: newPayload,
          onError: (errorPayload, error) => this.errorGenerateReport(errorPayload, error),
          onMailSuccess: () => {
            this.toaster.success('Your report will be shared via your email shortly');
            this.modal.close();
          },
        });
      },
      error: error => {
        this.errorGenerateReport(newPayload, error);
      },
    });
  }

  async pdfExport(data: any) {
    const exportPayload = { ...data };
    exportPayload.isMail = true;
    exportPayload.isExport = true;
    exportPayload.IsReportGenerated = false;
    exportPayload['APIToken'] = this.cookieService.get('token');
    exportPayload.indicatorId = exportPayload.indicatorId == '' ? 0 : exportPayload.indicatorId;

    const urlByFormat: Record<number, string> = {
      0: 'PDFGenerator/CustomReport?reuqestString',
      3: 'PDFGenerator/FullReport?reuqestString',
      4: 'PDFGenerator/CompactReport?requestString',
      11: 'PDFGenerator/ISOReport?reuqestString',
      12: 'PDFGenerator/ProductLCAReport?requestString',
    };

    const generateURL = urlByFormat[this.legacyReportFormatId];
    if (generateURL) {
      this.sendReportMail(exportPayload, generateURL);
    }
  }
}
